import type { IFormulationDescriptor } from "../descriptor";
import { isOfType } from "../utils";

export const Comparison = {
  GT: ">",
  GTE: "≥",
  LT: "<",
  LTE: "≤",
} as const;

export type RangeConstraint =
  | {
      min?: number;
      max: number;
    }
  | {
      min: number;
      max?: number;
    };

export interface StrictRange {
  min: number;
  max: number;
}

export type IConstraint =
  | ICategoricalConstraint
  | IFormulationConstraint
  | IIntegerConstraint
  | IScalarConstraint;

export type IIntegerConstraint = {
  type: "IntegerRange";
  descriptor_key: string;
} & RangeConstraint;

export type IScalarConstraint = {
  type: "ScalarRange";
  descriptor_key: string;
  min_inclusive: boolean;
  max_inclusive: boolean;
} & RangeConstraint;

export interface ICategoricalConstraint {
  type: "AcceptableCategoriesConstraint";
  descriptor_key: string;
  acceptable_classes: string[];
}

export interface IBaseFormulationConstraint extends StrictRange {
  formulation_descriptor: IFormulationDescriptor;
}

export interface IngredientRatioConstraint extends IBaseFormulationConstraint {
  type: "IngredientRatio";
  min: number;
  max: number;
  ingredients: Partial<Record<string, number>>;
  basis_ingredients: Partial<Record<string, number>>;
  labels: Partial<Record<string, number>>;
  basis_labels: Partial<Record<string, number>>;
}

export interface IBaseFormulationFractionConstraint extends IBaseFormulationConstraint {
  is_required?: boolean;
}

export interface IIngredientCountConstraint extends IBaseFormulationConstraint {
  type: "IngredientCountConstraint";
  label?: string;
}
// Added this type specifically for the Formulations design space editor,
// where only one IngredientCountConstraint will ever not have a label - all
// of the other IngredientCountConstraints will have labels. This is because
// orion is using the IngredientCountConstraint for both constraining the entire
// design space as well as constraining the number of ingredients in a label.
export type ILabelIngredientCountConstraint =
  Required<IIngredientCountConstraint>;

export interface IIngredientFractionConstraint extends IBaseFormulationFractionConstraint {
  type: "IngredientFractionConstraint";
  ingredient: string;
}

export interface ILabelFractionConstraint extends IBaseFormulationFractionConstraint {
  type: "LabelFractionConstraint";
  label: string;
}

export type TFormulationFractionConstraint =
  | IIngredientFractionConstraint
  | ILabelFractionConstraint;

export type IFormulationConstraint =
  | IIngredientCountConstraint
  | IIngredientFractionConstraint
  | ILabelFractionConstraint
  | ILabelIngredientCountConstraint
  | IngredientRatioConstraint;

export interface IBound {
  value: number;
  valid: boolean;
  message: string;
  minimum: number;
  maximum: number;
}

export const isCategoricalConstraint = isOfType<
  ICategoricalConstraint,
  IConstraint
>("AcceptableCategoriesConstraint");

export const isIngredientCountConstraint = isOfType<
  IIngredientCountConstraint,
  IConstraint
>("IngredientCountConstraint");

export const isLabelIngredientCountConstraint = (
  constraint: IConstraint,
): constraint is ILabelIngredientCountConstraint =>
  isIngredientCountConstraint(constraint) && Boolean(constraint.label);

export const isIngredientFractionConstraint = isOfType<
  IIngredientFractionConstraint,
  IConstraint
>("IngredientFractionConstraint");

export const isLabelFractionConstraint = isOfType<
  ILabelFractionConstraint,
  IConstraint
>("LabelFractionConstraint");

export const isFormulationFractionConstraint = (
  constraint: IConstraint,
): constraint is TFormulationFractionConstraint =>
  isIngredientFractionConstraint(constraint) ||
  isLabelFractionConstraint(constraint);

export const isIntegerRangeConstraint = isOfType<
  IIntegerConstraint,
  IConstraint
>("IntegerRange");

export const isScalarRangeConstraint = isOfType<IScalarConstraint, IConstraint>(
  "ScalarRange",
);

export const isIntegerOrScalarRangeConstraint = isOfType<
  IIntegerConstraint | IScalarConstraint,
  IConstraint
>("IntegerRange", "ScalarRange");

export const isCategoricalOrIntegerOrScalarRangeConstraint = isOfType<
  ICategoricalConstraint | IIntegerConstraint | IScalarConstraint,
  IConstraint
>("AcceptableCategoriesConstraint", "IntegerRange", "ScalarRange");

export const isFormulationConstraint = (
  constraint: IConstraint,
): constraint is IFormulationConstraint =>
  isIngredientCountConstraint(constraint) ||
  isIngredientFractionConstraint(constraint) ||
  isLabelFractionConstraint(constraint);

export const getDescriptorKeyFromConstraint = (
  constraint: IConstraint,
): string => {
  return isFormulationConstraint(constraint)
    ? constraint.formulation_descriptor.descriptor_key
    : constraint.descriptor_key;
};

export const isIngredientRatioConstraint = isOfType<
  IngredientRatioConstraint,
  IConstraint
>("IngredientRatio");

// can't do strict typing on input or it will flag "Categorical" as unmatchable
export function transformConstraint(constraint: {
  type: string;
  label?: string | null;
}): void {
  if (constraint.type === "Categorical") {
    constraint.type = "AcceptableCategoriesConstraint";
  }

  // constraint.label comes in as null from the server
  constraint.label = constraint.label ?? undefined;
}
